import { useState, useEffect, useRef, useCallback } from "react";
import { getPolls } from "../api/polls";
import { useTranslation } from "react-i18next";
import Loader from "./Loader";
import VoteModal from "./VoteModal";

const RequestedPolls = ({ votedPolls, setVotedPolls, isActive = true }) => {
  const { t } = useTranslation();
  const requestInProgress = useRef(false);
  const observerRef = useRef(null);
  const loaderRef = useRef(null);

  const [polls, setPolls] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Vote modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPoll, setSelectedPoll] = useState(null);

  // Expiry helper
  const isExpired = (expiry_datetime) => {
    if (!expiry_datetime) return false;
    return new Date(expiry_datetime) < new Date();
  };

  // Fetch requested polls
  const fetchRequestedPolls = useCallback(
    (pageNum) => {
      // Don't fetch if component is not active
      if (!isActive || requestInProgress.current) return;
      requestInProgress.current = true;
      setLoading(true);
      setError("");

      getPolls({ page: pageNum, type: 2 })
        .then((response) => {
          const newPolls = response?.data?.data || [];
          // No need to filter since server returns only requested polls when type: 2
          const apiLastPage = Number(response?.data?.last_page || 1);

          setPolls((prev) => {
            if (pageNum === 1) return newPolls;
            const ids = new Set(prev.map((p) => p.id));
            const filtered = newPolls.filter((p) => !ids.has(p.id));
            return [...prev, ...filtered];
          });

          setLastPage(apiLastPage);
        })
        .catch((err) => {
          setError(err?.message || "Failed to fetch requested polls");
        })
        .finally(() => {
          setLoading(false);
          requestInProgress.current = false;
        });
    },
    [isActive]
  );

  // Initial load
  useEffect(() => {
    fetchRequestedPolls(1);
  }, [fetchRequestedPolls]);

  // Load more when page changes
  useEffect(() => {
    if (page > 1) {
      fetchRequestedPolls(page);
    }
  }, [page, fetchRequestedPolls]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    // Only set up observer if component is active and mounted
    if (!loaderRef.current || !isActive) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const noMorePages = lastPage !== null && page >= lastPage;

        // Additional check to ensure component is still active
        if (
          entry.isIntersecting &&
          !loading &&
          !requestInProgress.current &&
          !noMorePages &&
          isActive
        ) {
          setPage((prev) => prev + 1);
        }
      },
      {
        root: null,
        rootMargin: "300px 0px",
        threshold: 0,
      }
    );

    observerRef.current.observe(loaderRef.current);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [loading, lastPage, page, isActive]);

  // Sort polls: expired polls at the end
  const sortedPolls = [...polls]
    .sort((a, b) => {
      // Recent first
      const aDate = new Date(a.created_at || a.expiry_datetime);
      const bDate = new Date(b.created_at || b.expiry_datetime);
      return bDate - aDate;
    })
    .sort((a, b) => {
      // Expired at the end
      const aExpired = isExpired(a.expiry_datetime);
      const bExpired = isExpired(b.expiry_datetime);
      if (aExpired === bExpired) return 0;
      return aExpired ? 1 : -1;
    });

  const openVoteModal = (poll) => {
    const latestPoll = polls.find((p) => p.id === poll.id) || poll;
    setSelectedPoll(latestPoll);
    setIsModalOpen(true);
  };

  const handleVoteSuccess = (updatedPoll) => {
    // Update the poll in the list
    setPolls((prevPolls) =>
      prevPolls.map((p) => (p.id === updatedPoll.id ? updatedPoll : p))
    );

    // Update selected poll if modal is still open
    setSelectedPoll(updatedPoll);
  };

  return (
    <>
      <div className="space-y-4">
        {error && <div className="text-center text-red-500">{error}</div>}

        {!error && sortedPolls.length === 0 && !loading && (
          <div className="text-center text-gray-500 dark:text-dark-text-secondary">{t("no_polls_found")}</div>
        )}

        {!error &&
          sortedPolls.map((poll) => (
      <div
  key={poll.id}
  className="bg-white dark:bg-[#252739] rounded-xl shadow p-4 flex flex-col gap-2 transition-colors duration-200 poll-card"
>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 relative">
                  <div className="relative">
                    <img
                      src={poll.user?.image || "/vibe_newlogo.png"}
                      alt={
                        poll.user?.full_name ||
                        poll.user?.user_name ||
                        poll.user?.email ||
                        "User"
                      }
                      className="w-10 h-10 rounded-full"
                    />
                    {/* Red dot for unvoted poll */}
                    {!poll.is_voted && !votedPolls.includes(poll.id) && (
                      <span
                        style={{
                          position: "absolute",
                          top: "-3px",
                          left: "-3px",
                          width: "14px",
                          height: "14px",
                          backgroundColor: "red",
                          borderRadius: "50%",
                          border: "2px solid white",
                          zIndex: 2,
                        }}
                        aria-label="Unvoted Poll"
                      />
                    )}
                  </div>
                  <span className="font-semibold text-gray-800 dark:text-dark-text">
                    {poll.user?.full_name ||
                      poll.user?.user_name ||
                      poll.user?.email ||
                      "User"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {isExpired(poll.expiry_datetime) && (
                    <span className="font-semibold text-sm text-red-600">
                      {t("expired")}
                    </span>
                  )}
                </div>
              </div>

              <p className="text-lg font-bold text-[#6d29f6]">
                {poll.poll_title || poll.question || "Poll"}
              </p>

              <button
                onClick={() => openVoteModal(poll)}
                className={`w-full py-2 rounded-lg font-semibold ${
                  poll.is_voted || votedPolls.includes(poll.id)
                    ? "bg-[#6d29f6] text-white"
                    : "bg-[#6d29f6] text-white"
                }`}
              >
                {poll.is_voted || votedPolls.includes(poll.id)
                  ? t("voted", "Voted")
                  : t("vote", "Vote")}
              </button>
            </div>
          ))}

        {loading && <Loader />}

        {/* Sentinel for IntersectionObserver */}
        <div ref={loaderRef} className="h-1" />
      </div>

      {/* Vote Modal */}
      <VoteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        poll={selectedPoll}
        onVoteSuccess={handleVoteSuccess}
        votedPolls={votedPolls}
        setVotedPolls={setVotedPolls}
      />
    </>
  );
};

export default RequestedPolls;
